import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Send as SendIcon,
  Description as TemplateIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Message as MessageIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import SmsEncodingNotice from './SmsEncodingNotice';
import { analyzeSmsEncoding } from '../utils/smsEncoding';

const MessageComposer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  
  const [messageData, setMessageData] = useState({
    recipient_type: 'first_timer',
    recipient_id: '',
    message_text: '',
    template_id: null
  });
  
  const [recipients, setRecipients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showRecipientDialog, setShowRecipientDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const smsAnalysis = analyzeSmsEncoding(messageData.message_text);

  // Load recipients and templates on component mount
  useEffect(() => {
    loadRecipients();
    loadTemplates();
  }, [messageData.recipient_type]);

  const loadRecipients = async () => {
    try {
      setLoading(true);
      let endpoint = '';
      
      if (messageData.recipient_type === 'first_timer') {
        endpoint = `${process.env.REACT_APP_API_URL}/api/follow-up/all?limit=100`;
      } else if (messageData.recipient_type === 'new_convert') {
        endpoint = `${process.env.REACT_APP_API_URL}/api/new-converts?limit=100`;
      } else if (messageData.recipient_type === 'harvest') {
        endpoint = `${process.env.REACT_APP_API_URL}/api/harvest/contacts?limit=100`;
      }

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const recipientsList = data.data || data.firstTimers || [];
        
        // Don't filter recipients here - let the backend handle invalid phone numbers
        // This allows us to capture and display failed messages properly
        const validRecipients = recipientsList;
        
        setRecipients(validRecipients);
      } else {
        setError('Failed to load recipients');
      }
    } catch (error) {
      console.error('Error loading recipients:', error);
      setError('Failed to load recipients');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sms/templates`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      } else {
        console.error('Failed to load templates');
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setMessageData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear success/error messages when user starts typing
    if (success || error) {
      setSuccess('');
      setError('');
    }
  };

  const handleTemplateSelect = (template) => {
    setMessageData(prev => ({
      ...prev,
      message_text: template.message_text,
      template_id: template.id
    }));
    setShowTemplateDialog(false);
  };

  const handleClearTemplate = () => {
    setMessageData(prev => ({
      ...prev,
      message_text: '',
      template_id: null
    }));
  };

  const handleRecipientSelect = (recipient) => {
    setMessageData(prev => ({
      ...prev,
      recipient_id: recipient.id
    }));
    setShowRecipientDialog(false);
  };

  const handleSendMessage = async () => {
    try {
      if (!messageData.recipient_id || !messageData.message_text.trim()) {
        setError('Please select a recipient and enter a message');
        return;
      }

      if (messageData.message_text.length > 320) {
        setError('Message cannot exceed 320 characters (2 SMS pages)');
        return;
      }

      setSending(true);
      setError('');
      setSuccess('');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sms/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(messageData)
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess('SMS sent successfully!');
        // Reset form
        setMessageData({
          recipient_type: 'first_timer',
          recipient_id: '',
          message_text: '',
          template_id: null
        });
      } else {
        // Check if SMS was actually sent despite the error
        if (result.smsSent) {
          setSuccess('SMS sent successfully! (Note: Message was sent but there was a minor database issue)');
          // Reset form
          setMessageData({
            recipient_type: 'first_timer',
            recipient_id: '',
            message_text: '',
            template_id: null
          });
        } else {
          setError(result.error || 'Failed to send SMS');
        }
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
      setError('Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const getSelectedRecipient = () => {
    return recipients.find(r => r.id === messageData.recipient_id);
  };

  const getSelectedTemplate = () => {
    return templates.find(t => t.id === messageData.template_id);
  };

  const getRecipientDisplayName = (recipient) => {
    if (messageData.recipient_type === 'first_timer') {
      return `${recipient.first_name} ${recipient.last_name}`;
    } else {
      return `${recipient.first_name} ${recipient.last_name}`;
    }
  };

  return (
    <Paper sx={{ p: 3, background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)' }}>
      <Typography variant="h5" component="h2" sx={{ mb: 3, color: '#DC2626', fontWeight: 600 }}>
        <MessageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Compose SMS Message
      </Typography>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Column - Message Form */}
        <Grid item xs={12} md={8}>
          <Box sx={{ mb: 3 }}>
            {/* Recipient Type Selection */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Recipient Type</InputLabel>
              <Select
                value={messageData.recipient_type}
                onChange={(e) => handleInputChange('recipient_type', e.target.value)}
                label="Recipient Type"
              >
                <MenuItem value="first_timer">First Timer</MenuItem>
                <MenuItem value="new_convert">New Convert</MenuItem>
                <MenuItem value="harvest">Harvest Contact</MenuItem>
              </Select>
            </FormControl>

            {/* Recipient Selection */}
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<PersonIcon />}
                onClick={() => setShowRecipientDialog(true)}
                fullWidth
                sx={{ justifyContent: 'space-between', py: 1.5 }}
              >
                {getSelectedRecipient() 
                  ? `${getRecipientDisplayName(getSelectedRecipient())} (${getSelectedRecipient().phone_number})`
                  : 'Select Recipient'
                }
              </Button>
            </Box>

            {/* Template Selection */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<TemplateIcon />}
                  onClick={() => setShowTemplateDialog(true)}
                  sx={{ flex: 1, justifyContent: 'space-between', py: 1.5 }}
                >
                  {getSelectedTemplate() 
                    ? `${getSelectedTemplate().name} (${getSelectedTemplate().category})`
                    : 'Select Template (Optional)'
                  }
                </Button>
                {getSelectedTemplate() && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleClearTemplate}
                    sx={{ minWidth: 'auto', px: 2 }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Box>

            {/* Message Text */}
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Message Text"
              value={messageData.message_text}
              onChange={(e) => handleInputChange('message_text', e.target.value)}
              placeholder="Type your message here... Use {{name}} for variables"
              helperText={`${messageData.message_text.length}/320 characters`}
              error={messageData.message_text.length > 320}
              sx={{ mb: 2 }}
            />
            <SmsEncodingNotice message={messageData.message_text} />
            
            {/* Variable Help Section */}
            <Box sx={{ 
              p: 2, 
              bgcolor: '#f8fafc', 
              borderRadius: 1, 
              border: '1px solid #e2e8f0',
              mb: 2 
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#374151' }}>
                📝 Available Variables You Can Use:
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}>
                    <strong>Personal Info:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', fontFamily: 'monospace' }}>
                    • {'{name}'} - Full name<br/>
                    • {'{firstName}'} - First name<br/>
                    • {'{lastName}'} - Last name<br/>
                    • {'{phoneNumber}'} - Phone number<br/>
                    • {'{email}'} - Email address<br/>
                    • {'{city}'} - City name
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}>
                    <strong>Dates & Events:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', fontFamily: 'monospace' }}>
                    • {'{serviceDate}'} - Date they visited<br/>
                    • {'{visitDate}'} - Same as serviceDate<br/>
                    • {'{conversionDate}'} - When they accepted Christ
                  </Typography>
                </Grid>
              </Grid>
              <Typography variant="caption" sx={{ color: '#94a3b8', mt: 1, display: 'block' }}>
                💡 Example: "Hello {'{firstName}'}, thank you for visiting us on {'{visitDate}'}!"
              </Typography>
            </Box>

            {/* Send Button */}
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
              onClick={handleSendMessage}
              disabled={sending || smsAnalysis.hasEmoji || !messageData.recipient_id || !messageData.message_text.trim()}
              fullWidth
              sx={{ 
                py: 1.5,
                background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #B91C1C 0%, #7F1D1D 100%)'
                }
              }}
            >
              {sending ? 'Sending...' : 'Send SMS'}
            </Button>
          </Box>
        </Grid>

        {/* Right Column - Message Preview */}
        <Grid item xs={12} md={4}>
          <Card sx={{ background: '#f9fafb', border: '1px solid #e5e7eb' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: '#374151' }}>
                Message Preview
              </Typography>
              
              {getSelectedRecipient() && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <PersonIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    Recipient:
                  </Typography>
                  <Chip 
                    label={getRecipientDisplayName(getSelectedRecipient())}
                    color="primary"
                    size="small"
                  />
                </Box>
              )}

              {getSelectedRecipient() && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <PhoneIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    Phone:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {getSelectedRecipient().phone_number}
                  </Typography>
                </Box>
              )}

              {messageData.message_text && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <MessageIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    Message:
                  </Typography>
                  <Paper sx={{ p: 2, background: '#ffffff', border: '1px solid #d1d5db' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {messageData.message_text}
                    </Typography>
                  </Paper>
                  <Typography 
                    variant="caption" 
                    color={messageData.message_text.length > 320 ? 'error' : 'text.secondary'}
                    sx={{ mt: 1, display: 'block' }}
                  >
                    {messageData.message_text.length}/320 characters
                    {messageData.message_text.length > 320 && ' (Exceeds limit!)'}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recipient Selection Dialog */}
      <Dialog 
        open={showRecipientDialog} 
        onClose={() => setShowRecipientDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Select {messageData.recipient_type === 'first_timer' ? 'First Timer' : 'New Convert'}
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {recipients.map((recipient) => (
                <ListItem 
                  key={recipient.id}
                  button
                  onClick={() => handleRecipientSelect(recipient)}
                  sx={{
                    '&:hover': { background: '#f3f4f6' },
                    borderBottom: '1px solid #e5e7eb'
                  }}
                >
                  <ListItemText
                    primary={getRecipientDisplayName(recipient)}
                    secondary={recipient.phone_number}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => handleRecipientSelect(recipient)}>
                      <PersonIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRecipientDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Template Selection Dialog */}
      <Dialog 
        open={showTemplateDialog} 
        onClose={() => setShowTemplateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Template</DialogTitle>
        <DialogContent>
          <List>
            {templates.map((template) => (
              <ListItem 
                key={template.id}
                button
                onClick={() => handleTemplateSelect(template)}
                sx={{
                  '&:hover': { background: '#f3f4f6' },
                  borderBottom: '1px solid #e5e7eb'
                }}
              >
                <ListItemText
                  primary={template.name}
                  secondary={
                    <Box>
                      <Chip 
                        label={template.category} 
                        size="small" 
                        color="primary" 
                        sx={{ mr: 1, mb: 1 }}
                      />
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {template.message_text}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTemplateDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default MessageComposer;
